import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';

import Hierarchy from '../../models/Hierarchy';
import { getFullName } from '../../models/User';

export interface HierarchyNodeData extends Hierarchy {
    isSelected: boolean;
    depth: number;
    childCount: number;
}

const _getInitials = (name: string, lastName: string | null): string => {
    const first = name?.charAt(0)?.toUpperCase() ?? '';
    const last = lastName?.charAt(0)?.toUpperCase() ?? '';
    return `${first}${last}`;
};

const HierarchyNode: React.FC<NodeProps<HierarchyNodeData>> = ({ data }) => {

    const [t] = useTranslation();
    const { name, last_name, email, rol, isSelected, depth, childCount } = data;

    const roles: string[] = rol?.map((r: string) => t(`rol_${r}`)) ?? [];
    const initials = _getInitials(name, last_name);
    const isRoot = depth === 0;

    return (
        <div className={`hf-node ${isSelected ? 'hf-node--selected' : ''} ${isRoot ? 'hf-node--root' : ''}`}>

            {/* Target handle (incoming connection from parent) */}
            {!isRoot && (
                <Handle
                    type="target"
                    position={Position.Top}
                    className="hf-node__handle"
                    isConnectable={false}
                />
            )}

            <div className="hf-node__inner">

                {/* Left: Avatar */}
                <div className={`hf-node__avatar ${isRoot ? 'hf-node__avatar--root' : ''}`}>
                    {initials}
                </div>

                {/* Right: Content */}
                <div className="hf-node__body">
                    <div className="hf-node__name" title={getFullName(data)}>
                        {getFullName(data)}
                    </div>
                    <div className="hf-node__email" title={email}>
                        {email}
                    </div>
                    {roles.length > 0 && (
                        <div className="hf-node__roles">
                            {roles.map((role, i) => (
                                <span key={i} className="hf-node__badge">
                                    {role}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

            </div>

            {/* Child count badge */}
            {childCount > 0 && !isSelected && (
                <div className="hf-node__count">
                    {childCount}
                </div>
            )}

            {/* Source handle (outgoing connection to children) */}
            {childCount > 0 && (
                <Handle
                    type="source"
                    position={Position.Bottom}
                    className="hf-node__handle"
                    isConnectable={false}
                />
            )}

        </div>
    );
};

export default React.memo(HierarchyNode);
